import React, { useEffect, useMemo, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { FaSearch, FaTimes } from "react-icons/fa";
import BlogCard from "./BlogCard";
import { Article } from "../../interfaces/Article";
import { slugify } from "../../functions/slugify";
import { Helmet } from "react-helmet-async";
import BlogPopular from "./BlogPopular";
import { useIntl } from "react-intl";

interface Props {
  locale: string;
}
const BlogList: React.FC<Props> = ({ locale }) => {
  const API_URL = process.env.REACT_APP_API_URL;
  const intl = useIntl();
  const { categorySlug } = useParams<{ categorySlug?: string }>();
  const [articles, setArticles] = useState<Article[]>([]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const queryFromUrl = searchParams.get("s") || "";

  const [localSearch, setLocalSearch] = useState(queryFromUrl);

  useEffect(() => {
    setIsLoading(true);
    fetch(`${API_URL}articles`)
      .then((res) => res.json())
      .then((json) => {
        let data = json.data;

        setArticles(data || []);
      })
      .catch((err) => console.error("Error fetching:", err))
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const activeCategoryName = useMemo(() => {
    const found = articles.find((a) => slugify(a.category) === categorySlug);
    return found ? found.category : categorySlug;
  }, [articles, categorySlug]);

  const handleSearchExecute = (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    if (localSearch.trim()) {
      navigate(`/blog?s=${encodeURIComponent(localSearch.trim())}`);
    } else {
      navigate("/blog");
    }
  };

  useEffect(() => {
    setLocalSearch(queryFromUrl);
  }, [queryFromUrl]);

  const filteredPosts = useMemo(() => {
    return articles
      .filter((post) => {
        const matchesSearch = queryFromUrl
          ? post.title.toLowerCase().includes(queryFromUrl.toLowerCase()) ||
            post.content.toLowerCase().includes(queryFromUrl.toLowerCase())
          : true;

        const matchesCategory = categorySlug
          ? slugify(post.category) === categorySlug
          : true;

        return matchesSearch && matchesCategory;
      })
      .sort((a, b) => {
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        return dateB - dateA;
      });
  }, [articles, queryFromUrl, categorySlug]);

  const categoryCounts = useMemo(() => {
    const counts: { [key: string]: number } = {};

    articles.forEach((art) => {
      if (art.category) {
        counts[art.category] = (counts[art.category] || 0) + 1;
      }
    });

    return counts;
  }, [articles]);

  const categories = useMemo(() => {
    return Object.keys(categoryCounts).sort();
  }, [categoryCounts]);

  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 2;

  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  const currentPosts = useMemo(() => {
    const lastIndex = currentPage * postsPerPage;
    const firstIndex = lastIndex - postsPerPage;
    return filteredPosts.slice(firstIndex, lastIndex);
  }, [filteredPosts, currentPage]);

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage, categorySlug]);

  useEffect(() => {
    setCurrentPage(1);
  }, [queryFromUrl, categorySlug]);

  return (
    <>
      {/* SEO */}
      <Helmet>
        <title>
          {categorySlug
            ? `${activeCategoryName} | Wooblazz`
            : queryFromUrl
              ? `${queryFromUrl} | Wooblazz`
              : "Blog | Wooblazz"}
        </title>
        <meta
          name="description"
          content={
            categorySlug
              ? `Kumpulan artikel mengenai ${activeCategoryName}`
              : "Blog resmi Wooblazz yang membahas CRM, AI Agent, dan Strategi Marketing."
          }
        />
        <meta
          property="og:title"
          content={
            categorySlug
              ? `${activeCategoryName} | Wooblazz`
              : queryFromUrl
                ? `${queryFromUrl} | Wooblazz`
                : "Blog | Wooblazz"
          }
        />
        <meta
          property="og:description"
          content={
            categorySlug
              ? `Kumpulan artikel mengenai ${activeCategoryName}`
              : "Blog resmi Wooblazz yang membahas CRM, AI Agent, dan Strategi Marketing."
          }
        />
      </Helmet>

      <div className="blog-banner-container">
        <div className="blog-banner-content">
          {(categorySlug || queryFromUrl) && (
            <div className="blog-banner-subtitle">
              {categorySlug
                ? intl.formatMessage({ id: "BLOG.CATEGORY" })
                : queryFromUrl
                  ? intl.formatMessage({ id: "BLOG.SEARCH_NOUN" })
                  : ""}
            </div>
          )}
          <h2 className="blog-banner-title">
            {activeCategoryName
              ? activeCategoryName
              : queryFromUrl
                ? queryFromUrl
                : "Blog"}
          </h2>
        </div>
      </div>
      <div className="container py-5">
        <div className="row">
          <div className="col-lg-8">
            <div className="w-lg-90 w-xl-80">
              {!categorySlug && !queryFromUrl && (
                <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                  <h3 className="fw-bold m-0">
                    {intl.formatMessage({ id: "BLOG.LATEST" })}
                  </h3>
                </div>
              )}

              {isLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : filteredPosts.length > 0 ? (
                <div className="row g-4">
                  {currentPosts.map((post) => (
                    <BlogCard post={post} key={post.id} locale={locale} />
                  ))}

                  {totalPages > 1 && (
                    <nav aria-label="Blog navigation" className="mt-5">
                      <ul className="pagination justify-content-center">
                        <li
                          className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                        >
                          <button
                            className="page-link shadow-sm px-3"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            &laquo;{" "}
                            <span className="d-none d-sm-inline">
                              {intl.formatMessage({ id: "BLOG.PREVIOUS" })}
                            </span>
                          </button>
                        </li>

                        {[...Array(totalPages)].map((_, index) => {
                          const pageNum = index + 1;
                          return (
                            <li
                              key={pageNum}
                              className={`page-item ${currentPage === pageNum ? "active" : ""}`}
                            >
                              <button
                                className="page-link shadow-sm px-3"
                                onClick={() => handlePageChange(pageNum)}
                              >
                                {pageNum}
                              </button>
                            </li>
                          );
                        })}

                        <li
                          className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                        >
                          <button
                            className="page-link shadow-sm px-3"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          >
                            <span className="d-none d-sm-inline">
                              {intl.formatMessage({ id: "BLOG.NEXT" })}
                            </span>{" "}
                            &raquo;
                          </button>
                        </li>
                      </ul>
                    </nav>
                  )}
                </div>
              ) : (
                <div className="text-center py-5 bg-light rounded-4">
                  <h4>{intl.formatMessage({ id: "BLOG.NOT_FOUND" })}</h4>
                  <p className="text-muted">
                    {intl.formatMessage({ id: "BLOG.OTHER_KEYWORD" })}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="col-lg-4 mt-5 mt-lg-0">
            <div className="" style={{ top: "100px", position: "sticky" }}>
              <div className="px-4 mb-4">
                <form onSubmit={handleSearchExecute}>
                  <div className="input-group custom-search-group shadow-sm">
                    <span className="input-group-text search-icon-box">
                      <FaSearch className="text-muted" />
                    </span>
                    <input
                      type="text"
                      className="form-control search-input"
                      placeholder={intl.formatMessage({ id: "BLOG.SEARCH" })}
                      value={localSearch}
                      onChange={(e) => setLocalSearch(e.target.value)}
                    />
                    {localSearch && (
                      <button
                        type="button"
                        className="btn clear-btn"
                        onClick={() => {
                          setLocalSearch("");
                          navigate("/blog");
                        }}
                      >
                        <FaTimes />
                      </button>
                    )}
                    <button
                      className="btn btn-primary search-btn"
                      type="submit"
                    >
                      {intl.formatMessage({ id: "BLOG.SEARCH_BUTTON" })}
                    </button>
                  </div>
                </form>
              </div>

              <div className="p-4 text-start">
                <div className="border-1 border-bottom">
                  <h5 className="fw-bold mb-3">
                    {intl.formatMessage({ id: "BLOG.CATEGORY" })}
                  </h5>
                </div>
                <div className="list-group list-group-flush">
                  <Link
                    to="/blog"
                    className={`d-flex justify-content-between align-items-center list-group-item list-group-item-action border-bottom py-3`}
                  >
                    <div
                      className={`${!categorySlug ? "text-primary fw-bold" : ""}`}
                    >
                      {intl.formatMessage({ id: "BLOG.ALL_CATEGORY" })}
                    </div>
                    <div className="text-muted small">({articles.length})</div>
                  </Link>
                  {categories.map((cat) => {
                    const currentSlug = slugify(cat);
                    return (
                      <Link
                        key={cat}
                        to={`/blog/category/${currentSlug}`}
                        className="d-flex justify-content-between align-items-center list-group-item list-group-item-action border-bottom py-3"
                      >
                        <div
                          className={`${categorySlug === currentSlug ? "text-primary fw-bold" : ""}`}
                        >
                          {cat}
                        </div>
                        <div className="text-muted small">
                          ({categoryCounts[cat] || 0})
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          <div className="col-12 mt-5">
            <BlogPopular articles={articles} />
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogList;
